import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";

type SearchableFilterOption = {
  value: string;
  label: string;
  searchText?: string;
  disabled?: boolean;
};

type SearchableFilterSelectProps = {
  value: string;
  options: SearchableFilterOption[];
  onChange: (value: string) => void;
  title?: string;
  containerClassName?: string;
  buttonClassName?: string;
  dropdownClassName?: string;
  searchPlaceholder: string;
  emptyLabel: string;
};

const normalizeSearchText = (value: string) =>
  value
    .toLocaleLowerCase("tr-TR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const SearchableFilterSelect: React.FC<SearchableFilterSelectProps> = ({
  value,
  options,
  onChange,
  title,
  containerClassName = "",
  buttonClassName = "",
  dropdownClassName = "",
  searchPlaceholder,
  emptyLabel,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const selectedOption =
    options.find((option) => option.value === value) || options[0] || null;

  const filteredOptions = useMemo(() => {
    const query = normalizeSearchText(searchQuery.trim());
    if (!query) {
      return options;
    }

    return options.filter((option) =>
      normalizeSearchText(
        `${option.label} ${option.searchText || option.value}`,
      ).includes(query),
    );
  }, [options, searchQuery]);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef} className={`relative shrink-0 ${containerClassName}`}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        title={title || selectedOption?.label || ""}
        className={`w-full h-[38px] rounded-lg border-2 bg-white/80 backdrop-blur-sm px-2 py-1 text-left text-[13px] shadow-sm transition-all flex items-center justify-between gap-2 ${buttonClassName}`}
      >
        <span className="truncate font-semibold">
          {selectedOption?.label || "-"}
        </span>
        <ChevronDownIcon
          className={`h-4 w-4 shrink-0 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div
          className={`absolute left-0 top-full mt-2 z-[90] min-w-full rounded-xl border border-gray-200 bg-white shadow-xl ${dropdownClassName || "w-72"}`}
        >
          <div className="relative border-b border-gray-100 px-3 py-2">
            <MagnifyingGlassIcon className="absolute left-5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={searchPlaceholder}
              className="w-full rounded-md border border-gray-200 py-1.5 pl-8 pr-2 text-sm text-gray-700 outline-none focus:border-green-400 focus:ring-2 focus:ring-green-200"
            />
          </div>
          <div className="max-h-64 overflow-auto py-1">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-3 text-sm text-gray-400">{emptyLabel}</div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = option.value === value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    disabled={option.disabled}
                    onClick={() => {
                      onChange(option.value);
                      setIsOpen(false);
                    }}
                    className={`flex w-full items-center px-3 py-2 text-left text-sm transition-colors ${
                      option.disabled
                        ? "cursor-not-allowed text-gray-300"
                        : isSelected
                          ? "bg-green-50 font-semibold text-green-700"
                          : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span className="truncate">{option.label}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
